import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import PageTitle from '../../../components/PageTitle';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getCompetition } from '../../../api/Competitions';
import { getVehiclesForBids, changeBidStatus } from '../../../api/Commerce';
import NavMenu from '../../navMenu/NavMenu';
import BiddingsTable from './BiddingsTable';
import Button from 'react-bootstrap/esm/Button';
import NewBidModal from './NewBidModal';
import EmptyState from '../../../components/EmptyState';
import CustomToast from '../../../components/CustomToast';
import VehicleBidCard from './VehicleBidCard';
import Loading from '../../../components/Loading';
import CompetitionStatus from '../CompetitionStatus';
import BiddingsButtonSection from './BiddingsButtonSection';

function CompetitionBiddings() {
  const [competition, setCompetition] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewBidModal, setShowNewBidModal] = useState(false);
  const [showBidSuccessToast, setShowBidSuccessToast] = useState(false);
  const [showChangeStatusError, setShowChangeStatusError] = useState(false);
  const { id: competitionId } = useParams();
  const location = useLocation();

  useEffect(() => {
    async function fetchData() {
      if (
        location.state === null ||
        Object.keys(location?.state?.competition).length === 0
      ) {
        const data = await getCompetition(competitionId);

        if (data) {
          setCompetition(data.competition);
        }
      } else {
        setCompetition(location.state.competition);
      }

      await fetchVehicles();
    }

    fetchData();
  }, [showNewBidModal]);

  async function fetchVehicles() {
    const fetchedVehicles = await getVehiclesForBids(competitionId);
    if (fetchedVehicles) {
      setVehicles(fetchedVehicles);
      setLoading(false);
    }
  }

  let biddingsSection;

  if (loading) {
    biddingsSection = (
      <div className="d-flex justify-content-center align-items-center mt-5">
        <Loading size="100px" text="Laddar anbud" />
      </div>
    );
  } else if (!loading && vehicles?.length === 0) {
    biddingsSection = (
      <EmptyState
        headingText="Inga fordon skapade"
        bodyText='Lägg till fordon under "Fordon" för att hantera anbud.'
        action={
          <Button
            as={Link}
            to={`/organizer/competitions/${competitionId}/vehicles`}
            variant="outline-primary"
          >
            Till Fordon
          </Button>
        }
      />
    );
  } else {
    biddingsSection = (
      <>
        <div className="d-none d-lg-flex">
          <BiddingsTable
            vehicles={vehicles}
            onChangeBidStatus={changeBidStatus}
            competitionId={competitionId}
            setShowChangeStatusError={setShowChangeStatusError}
            competitionStatus={competition?.status}
            setVehicles={setVehicles}
          />
        </div>

        <div className="d-flex flex-column gap-3 d-lg-none mt-2">
          {vehicles.map((v, index) => {
            return (
              <VehicleBidCard
                key={v.vehicleId}
                vehicle={v}
                setShowChangeStatusError={setShowChangeStatusError}
                competitionId={competitionId}
                competitionStatus={competition?.status}
                setVehicles={setVehicles}
              />
            );
          })}
        </div>
      </>
    );
  }

  return (
    <div>
      <Row>
        <Col>
          <PageTitle
            title={competition?.basicInformation?.name}
            includeLinkBack={true}
            linkBackUrl="/organizer/competitions"
            includeStatus={
              <CompetitionStatus
                status={competition?.status}
                id={competitionId}
              />
            }
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NavMenu competition={competition} />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <h4>Anbud</h4>
          <p className="text-secondary">
            Öppna fordon för att möjliggöra anbud. Stäng fordon för att
            möjliggöra lottning. Omlottning är möjligt efter första lottningen
            och kommer då exkludera det vinnande anbudet från första lottning,
            och så vidare.
          </p>
        </Col>
      </Row>
      <Row className="mt-4">
        <BiddingsButtonSection
          competitionId={competitionId}
          competitionStatus={competition?.status}
          vehicles={vehicles}
          setShowNewBidModal={setShowNewBidModal}
          fetchData={fetchVehicles}
        />
      </Row>
      <Row className="mt-2">
        <Col>
          <div className="d-flex gap-2 align-items-center">
            <div className="d-flex gap-2 align-items-center">
              <span className="text-secondary">Antal bilar med anbud</span>
              <span className="bg-light py-1 px-2">
                {vehicles.filter((v) => v.numberOfBids > 0)?.length ?? 0}
              </span>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <span className="text-secondary">Totalt antal anbud</span>
              <span className="bg-light py-1 px-2">
                {vehicles.reduce((acc, v) => acc + v.numberOfBids, 0)}
              </span>
            </div>
          </div>
        </Col>
      </Row>
      <Row className="mt-0">
        <Col>{biddingsSection}</Col>
      </Row>

      <NewBidModal
        vehicles={vehicles}
        setShowNewBidModal={setShowNewBidModal}
        showNewBidModal={showNewBidModal}
        competitionId={competitionId}
        showBidSuccessToast={setShowBidSuccessToast}
      />
      <CustomToast
        show={showBidSuccessToast}
        onClose={() => setShowBidSuccessToast(false)}
        type="success"
        title="Anbud tillagt!"
        body="Anbudet har lagts till."
      />
      <CustomToast
        show={showChangeStatusError}
        onClose={() => setShowChangeStatusError(false)}
        type="danger"
        title="Fel"
        body="Statusen kunde inte ändras. Vänligen försök igen senare."
      />
    </div>
  );
}

export default CompetitionBiddings;
